import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Tooltip
} from "@mui/material";
import {
  IoPencil,
  IoSave,
  IoClose,
  IoTrash,
  IoDocument,
  IoImage,
  IoDownload,
  IoCopy,
  IoCheckmark,
  IoSearch,
  IoCreateOutline,
  IoCopyOutline,
  IoCheckmarkDone
} from "react-icons/io5";
import ReactMarkdown from 'react-markdown';
import { preprocessMathDelimiters, remarkPlugins, rehypePlugins } from './markdownUtils';
import CodeExecutionResult from './CodeExecutionResult';
import DocumentGenerationResult from './DocumentGenerationResult';
import ProgressiveChainOfThoughtDisplay from './ProgressiveChainOfThoughtDisplay';
import ThinkingIndicator from './ThinkingIndicator';
import SearchSourcePills from './SearchSourcePills';
import config from '../../config';
import { adminColors, radius } from '../../theme/adminTheme';

const ChatMessage = ({
  msg,
  idx,
  editingMessageId,
  editingContent,
  setEditingContent,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onFileDelete,
  onFileDownload
}) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  const copyToClipboard = async (text, codeId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(codeId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyMessageText = async () => {
    try {
      await navigator.clipboard.writeText(msg.content || '');
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy message: ', err);
    }
  };

  const formatTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const CodeBlock = ({ children, className, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const codeId = `code-${idx}-${Math.random()}`;

    return (
      <Box sx={{ position: 'relative', my: 2 }}>
        <Paper
          sx={{
            backgroundColor: '#1e1e1e',
            borderRadius: radius.surface,
            overflow: 'hidden'
          }}
        >
          {/* Code header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              backgroundColor: '#2d2d2d',
              borderBottom: '1px solid #404040'
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#9ca3af', fontFamily: 'monospace' }}
            >
              {language || 'text'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(String(children).replace(/\n$/, ''), codeId)}
              sx={{
                color: '#9ca3af',
                '&:hover': { color: '#fff' }
              }}
            >
              {copiedCode === codeId ? <IoCheckmark size={14} /> : <IoCopy size={14} />}
            </IconButton>
          </Box>

          {/* Code content */}
          <Box
            component="pre"
            sx={{
              p: 2,
              m: 0,
              overflow: 'auto',
              backgroundColor: '#1e1e1e',
              color: '#e5e7eb',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '14px',
              lineHeight: 1.5
            }}
          >
            <code className={className} {...props}>
              {children}
            </code>
          </Box>
        </Paper>
      </Box>
    );
  };

  const isUser = msg.role === "user";
  const isEditing = editingMessageId === idx;
  const timeLabel = formatTime(msg.timestamp);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <IoImage size={16} />;
    return <IoDocument size={16} />;
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: "100%",
        py: 1.25,
        position: "relative",
        display: "flex",
        justifyContent: "center"
      }}
    >
      <Box
        sx={{
          maxWidth: "768px",
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start"
        }}
      >
        {/* Message content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isUser ? "flex-end" : "flex-start",
            maxWidth: isUser ? "75%" : "100%",
            minWidth: 0
          }}
        >
          <Box
            sx={{
              ...(isUser ? {
                backgroundColor: adminColors.accentLight,
                borderRadius: "20px",
                px: 2.25,
                py: 1.25,
                color: adminColors.ink
              } : {
                color: adminColors.ink,
                width: "100%"
              })
            }}
          >
            {isEditing ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "16px",
                      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }
                  }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={() => onSaveEdit(idx)}
                    size="small"
                    sx={{ color: adminColors.accent }}
                  >
                    <IoSave size={16} />
                  </IconButton>
                  <IconButton
                    onClick={onCancelEdit}
                    size="small"
                    sx={{ color: "#6b7280" }}
                  >
                    <IoClose size={16} />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <>
                {/* Show edited indicator */}
                {msg.edited && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7em',
                      color: isUser ? adminColors.inkSoft : '#6b7280',
                      fontStyle: 'italic',
                      display: 'block',
                      mb: 1,
                      background: isUser ? 'rgba(79,70,229,0.08)' : '#f3f4f6',
                      px: 1,
                      py: 0.3,
                      borderRadius: radius.control,
                      width: 'fit-content'
                    }}
                  >
                    ✏️ edited
                  </Typography>
                )}

                {/* Message indicators - only show for AI messages */}
                {!isUser && msg.used_search && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      background: '#ecfdf5',
                      px: 2,
                      py: 1,
                      borderRadius: radius.surface,
                      border: '1px solid #d1fae5'
                    }}
                  >
                    <IoSearch size={16} color="#059669" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                      Web Search Results
                    </Typography>
                  </Box>
                )}

                {!isUser && msg.used_documents && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      background: '#eff6ff',
                      px: 2,
                      py: 1,
                      borderRadius: radius.surface,
                      border: '1px solid #dbeafe'
                    }}
                  >
                    <IoDocument size={16} color="#0ea5e9" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0ea5e9' }}>
                      Document Analysis
                    </Typography>
                  </Box>
                )}

                {!isUser && msg.used_uploaded_files && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      background: '#fef3c7',
                      px: 2,
                      py: 1,
                      borderRadius: radius.surface,
                      border: '1px solid #fed7aa'
                    }}
                  >
                    <IoImage size={16} color="#f59e0b" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                      Uploaded Files Analyzed
                    </Typography>
                  </Box>
                )}

                {/* Loading indicator - shows the step the backend is actually on
                  right now (from live SSE "step" events), not a generic
                  message that never changes until everything is done. */}
                {!isUser && (msg.isLoading || msg.thinkingDurationMs != null) && (
                  <ThinkingIndicator
                    isThinking={!!msg.isLoading}
                    label={msg.isLoading ? msg.content : null}
                    steps={(() => {
                      const rawSteps = msg.steps || [];
                      if (!msg.isLoading) return rawSteps;
                      // While loading, the latest PLAIN step is already shown
                      // as the shimmering label above, so drop it here to
                      // avoid duplicating it in the collapsed details list.
                      // But a step carrying searchMeta must never be dropped
                      // this way - it's the persistent web-search card, and
                      // it stays the last entry for the rest of the wait, so
                      // slicing it off here would hide it for the entire
                      // loading phase (the bug where only "Thinking"/
                      // reassurance text ever showed, even though the real
                      // search step had already arrived).
                      const last = rawSteps[rawSteps.length - 1];
                      return typeof last === "string" ? rawSteps.slice(0, -1) : rawSteps;
                    })()}
                    durationMs={msg.thinkingDurationMs}
                  />
                )}

                {/* Message text - while loading, ThinkingIndicator above already
                  shows the wait state, so nothing is rendered here yet. */}
                {!msg.isLoading && msg.content && (
                  <Box
                    // liveError (set in useChatActions.js alongside isError)
                    // means this failure was just created in the current
                    // session, not hydrated from chat history - only those
                    // get the assertive alert role. Without this gate,
                    // opening an old thread with several past failures would
                    // make a screen reader announce all of them at once on
                    // load; historical isError rows still get the visual
                    // card below, just without role="alert".
                    role={msg.isError && msg.liveError ? "alert" : undefined}
                    sx={{
                      fontSize: "15.5px",
                      lineHeight: 1.6,
                      color: adminColors.ink,
                      // Both roles share the app's sans stack; the codebase used to
                      // fall back to an unloaded ChatGPT-style display font (and a
                      // serif fallback for assistant replies) which leaked the
                      // browser default serif.
                      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      // Image-generation failures (useChatActions.js tags these
                      // isError:true) otherwise render as an ordinary assistant
                      // bubble - the only signal is a ❌ character buried in the
                      // text. Give them a tinted card in the danger-red family
                      // adminColors/adminTheme use for failure states
                      // (SafetyFlagsView's "high" severity, palette.error),
                      // instead of a one-off red, so it reads at a glance
                      // without relying on the ❌ glyph alone - the same
                      // errorLight/errorBorder/error tokens CodeExecutionResult
                      // and DocumentGenerationResult's failure states now use,
                      // so every "this failed" card in the app is one palette.
                      // Paragraphs below don't set their own color, so this
                      // is inherited by the markdown body - no separate "& p"
                      // override needed.
                      ...(msg.isError && {
                        backgroundColor: adminColors.errorLight,
                        border: `1px solid ${adminColors.errorBorder}`,
                        borderRadius: radius.surface,
                        color: adminColors.errorDark,
                        px: 2,
                        py: 1.5
                      }),
                      "& p": { margin: 0, mb: 2 },
                      "& p:last-child": { mb: 0 },
                      "& ul, & ol": { pl: 2, mb: 2 },
                      "& li": { mb: 0.5 },
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        mb: 2,
                        mt: 3,
                        "&:first-of-type": { mt: 0 },
                        color: "inherit"
                      },
                      "& h1": { fontSize: "1.15rem", fontWeight: 700 },
                      "& h2": { fontSize: "1.1rem", fontWeight: 700 },
                      "& h3": { fontSize: "1rem", fontWeight: 650 },
                      "& h4, & h5, & h6": { fontSize: "0.95rem", fontWeight: 600 },
                      "& blockquote": {
                        borderLeft: "4px solid rgba(24,24,27,0.12)",
                        pl: 2,
                        ml: 0,
                        fontStyle: "italic",
                        color: adminColors.inkSoft
                      },
                      // GFM tables: rendered <table> is a block element with
                      // no wrapper, so overflow-x has to live on the table
                      // itself (the same "display: block" trick GitHub's own
                      // markdown CSS uses) or a wide table blows out the
                      // 768px-capped bubble instead of scrolling internally.
                      "& table": {
                        display: "block",
                        width: "max-content",
                        maxWidth: "100%",
                        overflowX: "auto",
                        borderCollapse: "collapse",
                        my: 2,
                        fontSize: "0.92em"
                      },
                      "& th, & td": {
                        border: "1px solid rgba(24,24,27,0.12)",
                        px: 1.5,
                        py: 0.75,
                        textAlign: "left"
                      },
                      "& th": {
                        fontWeight: 600,
                        backgroundColor: "rgba(24,24,27,0.03)"
                      },
                      "& tr:nth-of-type(even) td": {
                        backgroundColor: "rgba(24,24,27,0.015)"
                      },
                      ...(msg.isStreaming && {
                        "& p:last-of-type::after": {
                          content: '""',
                          display: "inline-block",
                          width: "7px",
                          height: "15px",
                          ml: "2px",
                          verticalAlign: "text-bottom",
                          backgroundColor: adminColors.accent,
                          animation: "blinkCursor 0.9s step-end infinite",
                          "@media (prefers-reduced-motion: reduce)": {
                            animation: "none",
                            opacity: 1
                          }
                        },
                        "@keyframes blinkCursor": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0 }
                        }
                      })
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={remarkPlugins}
                      rehypePlugins={rehypePlugins}
                      components={{
                        code: CodeBlock,
                        pre: ({ children }) => <Box>{children}</Box>,
                        a: ({ href, children }) => {
                          // Check if this is a document download link
                          const isDocumentLink = href && (
                            href.includes('.pdf') ||
                            href.includes('.docx') ||
                            href.includes('/download/') ||
                            href.includes('document') ||
                            href.includes('storage/') ||
                            href.includes('uploads/')
                          );

                          if (isDocumentLink) {
                            return (
                              <Box
                                component="span"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = href;
                                  link.download = String(children) || 'document';
                                  link.target = '_blank';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                sx={{
                                  color: adminColors.accent,
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  "&:hover": {
                                    color: adminColors.accentDark,
                                    textDecoration: "none"
                                  }
                                }}
                              >
                                <IoDocument size={14} />
                                {children}
                              </Box>
                            );
                          }

                          // Regular link
                          return (
                            <Typography
                              component="a"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: adminColors.accent,
                                textDecoration: 'underline',
                                fontWeight: 500,
                                '&:hover': {
                                  textDecoration: 'none',
                                  color: adminColors.accentDark
                                }
                              }}
                            >
                              {children}
                            </Typography>
                          );
                        }
                      }}
                    >
                      {preprocessMathDelimiters(msg.content)}
                    </ReactMarkdown>

                    {/* Source pills - real per-URL citations from the web
                      search, either from the live SSE `done` payload
                      (searchResults) or, after a page refresh, from
                      GET /chats/{id}/messages (search_citations) - see
                      useChatHistory.js's loadHistory. */}
                    <SearchSourcePills sources={msg.searchResults || msg.search_citations} />
                  </Box>
                )}

                {/* Files - handle both new and legacy formats (excluding documents handled separately) */}
                {((msg.attachedFiles && msg.attachedFiles.length > 0) || msg.uploadedFile || (msg.files && msg.files.length > 0)) &&
                  !msg.document_url && !msg.document_filename && !msg.is_document_result && (
                    <Box sx={{ mt: 2 }}>
                      {/* Handle multiple attached files */}
                      {msg.attachedFiles?.map((file, fileIdx) => (
                        <Box key={file.id || fileIdx} sx={{ mb: fileIdx < msg.attachedFiles.length - 1 ? 2 : 0 }}>
                          {/* Image Preview for image files */}
                          {file.type?.startsWith('image/') && file.url && (
                            <Box sx={{ mb: 1.5 }}>
                              <img
                                src={file.url}
                                alt={file.name}
                                style={{
                                  maxWidth: '250px',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  border: '1px solid #e5e7eb',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </Box>
                          )}

                          <Paper
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              backgroundColor: "#f9fafb",
                              border: "1px solid #e5e7eb"
                            }}
                          >
                            {getFileIcon(file.type)}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      ))}

                      {/* Handle single legacy uploadedFile if no attachedFiles */}
                      {!msg.attachedFiles && msg.uploadedFile && (
                        <Box>
                          {/* Image Preview for image files */}
                          {msg.uploadedFile.type?.startsWith('image/') && msg.uploadedFile.url && (
                            <Box sx={{ mb: 1.5 }}>
                              <img
                                src={msg.uploadedFile.url}
                                alt={msg.uploadedFile.name}
                                style={{
                                  maxWidth: '250px',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  border: '1px solid #e5e7eb',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </Box>
                          )}

                          <Paper
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              backgroundColor: "#f9fafb",
                              border: "1px solid #e5e7eb"
                            }}
                          >
                            {getFileIcon(msg.uploadedFile.type)}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                                {msg.uploadedFile.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                {formatFileSize(msg.uploadedFile.size)}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}

                      {/* Handle new format files */}
                      {!msg.attachedFiles && !msg.uploadedFile && msg.files?.map((file, fileIdx) => (
                        <Paper
                          key={fileIdx}
                          sx={{
                            p: 2,
                            mt: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            backgroundColor: "#f9fafb",
                            border: "1px solid #e5e7eb"
                          }}
                        >
                          {getFileIcon(file.type)}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                              {file.filename || file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => onFileDownload && onFileDownload(file)}
                            sx={{ color: "#6b7280" }}
                          >
                            <IoDownload size={16} />
                          </IconButton>

                          {!isUser && (
                            <IconButton
                              size="small"
                              onClick={() => onFileDelete && onFileDelete(file)}
                              sx={{ color: "#dc2626" }}
                            >
                              <IoTrash size={16} />
                            </IconButton>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}

                {/* Display generated image if available */}
                {msg.isImageResult && msg.imageUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      component="img"
                      src={msg.imageUrl}
                      alt={msg.imagePrompt || "Generated image"}
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: radius.surface,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}
                      onClick={() => window.open(msg.imageUrl, '_blank')}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = msg.imageUrl;
                          link.download = `generated-image-${msg.jobId || Date.now()}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        sx={{ color: "#6b7280" }}
                      >
                        <IoDownload size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => window.open(msg.imageUrl, '_blank')}
                        sx={{ color: "#6b7280" }}
                      >
                        <IoImage size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Display document card if available */}
                {(msg.document_url || msg.document_filename || msg.is_document_result) && (
                  <Box sx={{ mt: 2 }}>
                    <Paper
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: radius.surface,
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      {/* Document Icon */}
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: radius.control,
                          background: msg.is_document_result
                            ? '#8b5cf6'
                            : '#0ea5e9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                      >
                        <IoDocument size={24} />
                      </Box>

                      {/* Document Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {msg.document_filename || "Document"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#6b7280",
                            display: 'block',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 500
                          }}
                        >
                          {msg.is_document_result ? "Generated Document" : "Uploaded Document"}
                        </Typography>
                        {msg.document_type && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              display: 'block',
                              mt: 0.5
                            }}
                          >
                            {msg.document_type.toUpperCase()}
                          </Typography>
                        )}
                      </Box>

                      {/* Action Buttons */}
                      {msg.document_url && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Convert file path to proper download URL
                              let downloadUrl = msg.document_url;
                              if (downloadUrl.startsWith('/opt/code/download/')) {
                                // Extract filename and use the download endpoint
                                const filename = downloadUrl.split('/').pop();
                                downloadUrl = `${config.API_BASE_URL}/api/code/download/${filename}`;
                              } else if (downloadUrl.startsWith('/download/')) {
                                // Already a proper download path, make it absolute
                                downloadUrl = `${config.API_BASE_URL}/api/code${downloadUrl}`;
                              } else if (downloadUrl.startsWith('/api/code/download/')) {
                                // Already correct path, make it absolute
                                downloadUrl = `${config.API_BASE_URL}${downloadUrl}`;
                              } else if (downloadUrl.startsWith('/')) {
                                // Other relative path, make it absolute
                                downloadUrl = `${config.API_BASE_URL}${downloadUrl}`;
                              }

                              // Simple approach like the old implementation
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = msg.document_filename || 'document';
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            sx={{ color: "#6b7280", '&:hover': { color: "#374151" } }}
                          >
                            <IoDownload size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              let viewUrl = msg.document_url;
                              if (viewUrl.startsWith('/opt/code/download/')) {
                                const filename = viewUrl.split('/').pop();
                                viewUrl = `${config.API_BASE_URL}/api/code/download/${filename}`;
                              } else if (viewUrl.startsWith('/download/')) {
                                viewUrl = `${config.API_BASE_URL}/api/code${viewUrl}`;
                              } else if (viewUrl.startsWith('/api/code/download/')) {
                                viewUrl = `${config.API_BASE_URL}${viewUrl}`;
                              } else if (viewUrl.startsWith('/')) {
                                viewUrl = `${config.API_BASE_URL}${viewUrl}`;
                              }

                              window.open(viewUrl, '_blank');
                            }}
                            sx={{ color: "#6b7280", '&:hover': { color: "#374151" } }}
                          >
                            <IoDocument size={16} />
                          </IconButton>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )}

                {/* Chain of thought - move this before code execution results */}
                {(msg.reasoning || (msg.codeExecutionResult && msg.codeExecutionResult.reasoning) || (msg.code_execution_result && msg.code_execution_result.reasoning)) && (
                  <Box sx={{ mt: 2 }}>
                    <ProgressiveChainOfThoughtDisplay
                      reasoning={msg.reasoning || msg.codeExecutionResult?.reasoning || msg.code_execution_result?.reasoning}
                      isComplete={true}
                      currentStep={99}
                      isUserMessage={isUser}
                      compact={true}
                    />
                  </Box>
                )}

                {/* Code execution results - handle both old and new formats */}
                {(msg.code_execution_result || msg.codeExecutionResult) && (
                  <Box sx={{ mt: 2 }}>
                    {/* Check if this is a document/presentation generation result */}
                    {((msg.code_execution_result?.output_type === 'presentation') ||
                      (msg.codeExecutionResult?.output_type === 'presentation') ||
                      (msg.code_execution_result?.artifacts &&
                        msg.code_execution_result.artifacts.some(artifact =>
                          artifact.type === 'presentation' || artifact.type === 'document'
                        )) ||
                      (msg.codeExecutionResult?.artifacts &&
                        msg.codeExecutionResult.artifacts.some(artifact =>
                          artifact.type === 'presentation' || artifact.type === 'document'
                        ))) ? (
                      <DocumentGenerationResult result={msg.code_execution_result || msg.codeExecutionResult} />
                    ) : (
                      <CodeExecutionResult result={msg.code_execution_result || msg.codeExecutionResult} />
                    )}
                  </Box>
                )}

                {/* Document generation results - handle both old and new formats */}
                {(msg.document_generation_result || msg.documentGenerationResult) && (
                  <Box sx={{ mt: 2 }}>
                    <DocumentGenerationResult result={msg.document_generation_result || msg.documentGenerationResult} />
                  </Box>
                )}

              </>
            )}
          </Box>

          {/* Timestamp + hover actions, below the bubble */}
          {!isEditing && !msg.isLoading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mt: 0.5,
                px: 0.5,
                minHeight: 22,
                flexDirection: isUser ? "row-reverse" : "row"
              }}
            >
              {timeLabel && (
                <Typography
                  variant="caption"
                  sx={{ color: "#9ca3af", fontSize: "11.5px", whiteSpace: "nowrap" }}
                >
                  {timeLabel}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 0.25,
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.15s ease",
                  pointerEvents: isHovered ? "auto" : "none"
                }}
              >
                {msg.content && (
                  <Tooltip title={messageCopied ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={copyMessageText}
                      sx={{
                        color: "#9ca3af",
                        p: 0.5,
                        "&:hover": { backgroundColor: "#f3f4f6", color: "#374151" }
                      }}
                    >
                      {messageCopied ? <IoCheckmarkDone size={13} /> : <IoCopyOutline size={13} />}
                    </IconButton>
                  </Tooltip>
                )}
                {isUser && (
                  <Tooltip title="Edit message">
                    <IconButton
                      size="small"
                      onClick={() => onStartEdit(idx, msg.content)}
                      sx={{
                        color: "#9ca3af",
                        p: 0.5,
                        "&:hover": { backgroundColor: "#f3f4f6", color: "#374151" }
                      }}
                    >
                      <IoCreateOutline size={13} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;
